import type { GATrackingOptions, ModuleRule } from './types'

const PAYMENT_PROVIDER_MODULE_RULES: ReadonlyArray<ModuleRule> = ['airwallex', 'stripe'].map(
  (provider) => ({
    selector: `[data-payment-provider="${provider}"]`,
    module: `payment_${provider}`,
  })
)

/**
 * PayKit 模块识别规则配置
 * 按优先级排序，越靠前的规则优先级越高
 */
export const DEFAULT_MODULE_RULES: ReadonlyArray<ModuleRule> = [
  // 结账流程主要容器
  {
    selector: '.checkout-header',
    module: 'checkout_header',
  },
  {
    selector: '.checkout-form',
    module: 'checkout_form',
  },
  {
    selector: '.order-summary',
    module: 'order_summary',
  },
  {
    selector: '.payment-method-selector',
    module: 'payment_methods',
  },
  {
    selector: '.customer-info-form',
    module: 'customer_info',
  },
  {
    selector: '.coupon-input',
    module: 'coupon',
  },
  {
    selector: '.currency-selector',
    module: 'currency_selector',
  },
  {
    selector: '.language-switcher',
    module: 'language_switcher',
  },
  // 支付提供商容器
  ...PAYMENT_PROVIDER_MODULE_RULES,
  // Payment card form
  {
    selector: '.split-card-form, [data-payment-type="card"]',
    module: 'payment_card',
  },
  // Apple Pay
  {
    selector: '.apple-pay-button, [data-payment-type="applepay"]',
    module: 'payment_applepay',
  },
  // Google Pay
  {
    selector: '.google-pay-button, [data-payment-type="googlepay"]',
    module: 'payment_googlepay',
  },
  // 通用卡片容器（动态识别）
  {
    selector: '[data-ga-module]',
    module: 'dynamic',
    handler: (element: Element): string => {
      return element.getAttribute('data-ga-module') || 'unknown'
    },
  },
] as const

/**
 * 追踪引擎内部配置常量
 */
export const TRACKING_CONFIG = {
  /** 点击防抖时间 (ms) */
  CLICK_DEBOUNCE_TIME: 5000,
  /** 最大向上查找深度 */
  MAX_DEPTH_SEARCH: 5,
  /** 模块识别最大深度 */
  MODULE_DETECTION_DEPTH: 10,
  /** 移动端断点 */
  MOBILE_BREAKPOINT: 768,
  /** 平板断点 */
  TABLET_BREAKPOINT: 1024,
} as const

/**
 * 判断是否为开发环境
 */
const isDevEnvironment = (): boolean => {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as { env?: { DEV?: boolean; MODE?: string } }).env
    return Boolean(env?.DEV) || env?.MODE === 'development'
  }
  return false
}

/**
 * GA 追踪默认配置
 */
export const DEFAULT_GA_CONFIG: Required<GATrackingOptions> = {
  businessEventContract: null,
  // 白名单选择器：匹配这些选择器的元素会被自动追踪
  whitelist: [
    // 链接
    'a',
    '[role="link"]',
    // 按钮
    'button',
    '[role="button"]',
    '.cursor-pointer',
    // 表单元素交互
    '[type="radio"]',
    '[type="checkbox"]',
    // 自定义追踪标记
    '[data-ga-track]',
    '[data-ga-action-name]',
    '[data-ga-module]',
    // PayKit 特定组件
    '.payment-method-item',
    '.currency-option',
    '.language-option',
  ],

  // 黑名单选择器：匹配这些选择器的元素不会被追踪
  blacklist: [
    "input:not([type='radio']):not([type='checkbox'])",
    'textarea',
    'select',
    '[contenteditable="true"]',
    '.no-track',
    '[data-no-track]',
    '[data-ga-track="false"]',
    '[href="#"]',
    '[href^="javascript:"]',
    'body',
    'html',
  ],

  // 内部域名列表
  internalDomains: [
    'localhost',
    '127.0.0.1',
    // VigilKids
    'vigilkids.com',
    'pay.vigilkids.com',
    'checkout.vigilkids.com',
    // Visiva
    'visiva.ai',
    'pay.visiva.ai',
    'checkout.visiva.ai',
  ],

  // 仅追踪不修改 URL 的链接类型
  trackOnlyTypes: ['external', 'tel', 'mailto', 'anchor', 'javascript'],

  // 模块名称前缀 (默认为空，由品牌配置覆盖)
  modulePrefix: '',

  // 模块识别规则
  moduleRules: DEFAULT_MODULE_RULES,

  // 开发环境开启调试模式
  debug: isDevEnvironment(),
}

/**
 * 合并用户配置和默认配置
 */
export function mergeConfig(userConfig: GATrackingOptions = {}): Required<GATrackingOptions> {
  return {
    whitelist: userConfig.whitelist ?? DEFAULT_GA_CONFIG.whitelist,
    blacklist: userConfig.blacklist ?? DEFAULT_GA_CONFIG.blacklist,
    internalDomains: userConfig.internalDomains ?? DEFAULT_GA_CONFIG.internalDomains,
    trackOnlyTypes: userConfig.trackOnlyTypes ?? DEFAULT_GA_CONFIG.trackOnlyTypes,
    modulePrefix: userConfig.modulePrefix ?? DEFAULT_GA_CONFIG.modulePrefix,
    moduleRules: userConfig.moduleRules ?? DEFAULT_GA_CONFIG.moduleRules,
    businessEventContract:
      userConfig.businessEventContract ?? DEFAULT_GA_CONFIG.businessEventContract,
    debug: userConfig.debug ?? DEFAULT_GA_CONFIG.debug,
  }
}

/**
 * 验证配置有效性
 */
export function validateConfig(config: GATrackingOptions): boolean {
  if (config.whitelist && !Array.isArray(config.whitelist)) {
    console.warn('[GA Tracking] whitelist must be an array')
    return false
  }

  if (config.blacklist && !Array.isArray(config.blacklist)) {
    console.warn('[GA Tracking] blacklist must be an array')
    return false
  }

  return true
}

export type { ModuleRule }
